import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, UserPlus, Users } from 'lucide-react';
import { listMspAdmins, createUser, updateUserStatus, resetUserPassword } from '../api/users';
import { useAuth } from '../auth/useAuth';
import { apiErrorMessage } from '../lib/api';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../components/ui/Table';
import { StatusBadge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import type { AppUser } from '../types/api';

// New Oakford staff account modal. Deliberately separate from
// customers/NewUserModal - there's no org/site role picker here, since an
// MSP Admin already has access to everything (that's the whole point).
function NewMspUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setError(null);
  }

  const mutation = useMutation({
    mutationFn: () =>
      createUser({
        email,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        password,
        isMspAdmin: true,
      }),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['msp-admins'] });
      showToast(`${user.email} created as an MSP Admin.`);
      reset();
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not create this user.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New MSP user"
      size="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button form="new-msp-user-form" type="submit" isLoading={mutation.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form id="new-msp-user-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Alert tone="info">
          This grants full Oakford staff access - every customer, every site, every setting. Only use it for
          Oakford's own team.
        </Alert>
        <Input label="Email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <Input
          label="Initial password"
          type="password"
          required
          minLength={8}
          hint="At least 8 characters. Share this with them directly so they can sign in."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </form>
    </Modal>
  );
}

// Admin-set password reset for an MSP Admin - same action as
// EditUserModal's inline password field, split out into its own small
// modal here since there's no roles form to bundle it with.
function ResetMspPasswordModal({ user, open, onClose }: { user: AppUser | null; open: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => resetUserPassword(user!.id, password),
    onSuccess: () => {
      showToast(`Password reset for ${user!.email}.`);
      setPassword('');
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not reset this password.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    mutation.mutate();
  }

  function handleClose() {
    setPassword('');
    setError(null);
    onClose();
  }

  if (!user) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Reset password - ${user.email}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button form="reset-msp-password-form" type="submit" isLoading={mutation.isPending}>
            Set password
          </Button>
        </>
      }
    >
      <form id="reset-msp-password-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Input
          label="New password"
          type="password"
          required
          autoFocus
          minLength={8}
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </form>
    </Modal>
  );
}

// MSP-level user management (§16) - lets an existing MSP Admin add, view
// status, deactivate/reactivate and reset the password of Oakford's own
// staff accounts. Route-guarded to MSP Admin in App.tsx; the underlying
// GET /users/msp-admins and POST /users (isMspAdmin: true) endpoints are
// @Roles(MSP_ADMIN)-checked (or, for POST, checked against the acting
// user inside the service) too, so this is defence in depth, not the only
// guard.
export function MspUsersPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<AppUser | null>(null);
  const [statusTarget, setStatusTarget] = useState<AppUser | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const usersQuery = useQuery({ queryKey: ['msp-admins'], queryFn: listMspAdmins });

  const statusMutation = useMutation({
    mutationFn: () => updateUserStatus(statusTarget!.id, statusTarget!.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
    onSuccess: (u) => {
      queryClient.invalidateQueries({ queryKey: ['msp-admins'] });
      showToast(`${u.email} ${u.status === 'ACTIVE' ? 'reactivated' : 'deactivated'}.`);
      setStatusTarget(null);
    },
    onError: (err) => setStatusError(apiErrorMessage(err, 'Could not update this user.')),
  });

  return (
    <div>
      <PageHeader title="MSP users" description="Oakford staff accounts with full platform access." />

      <Card>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <span className="text-sm font-medium text-slate-600">
            {usersQuery.data?.length ?? 0} MSP user{usersQuery.data?.length === 1 ? '' : 's'}
          </span>
          <Button size="sm" onClick={() => setNewModalOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" /> New MSP user
          </Button>
        </div>
        {usersQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !usersQuery.data || usersQuery.data.length === 0 ? (
          <EmptyState icon={Users} title="No MSP users yet" description="Add Oakford staff accounts here." />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Email</Th>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </Thead>
            <tbody>
              {usersQuery.data.map((u) => (
                <Tr key={u.id}>
                  <Td className="font-medium text-slate-900">{u.email}</Td>
                  <Td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '-'}</Td>
                  <Td>
                    <StatusBadge status={u.status} kind="user" />
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setResetTarget(u)}>
                        <KeyRound className="h-3.5 w-3.5" /> Reset password
                      </Button>
                      {u.status !== 'LOCKED' && u.id !== currentUser?.id && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setStatusError(null);
                            setStatusTarget(u);
                          }}
                        >
                          {u.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <NewMspUserModal open={newModalOpen} onClose={() => setNewModalOpen(false)} />
      <ResetMspPasswordModal user={resetTarget} open={!!resetTarget} onClose={() => setResetTarget(null)} />

      <ConfirmModal
        open={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        onConfirm={() => statusMutation.mutate()}
        title={statusTarget?.status === 'ACTIVE' ? 'Deactivate MSP user' : 'Reactivate MSP user'}
        description={
          statusTarget?.status === 'ACTIVE'
            ? `${statusTarget?.email} will no longer be able to sign in. This can be reversed at any time.`
            : `${statusTarget?.email} will regain full Oakford staff access.`
        }
        confirmLabel={statusTarget?.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
        tone={statusTarget?.status === 'ACTIVE' ? 'danger' : 'primary'}
        isLoading={statusMutation.isPending}
        error={statusError}
      />
    </div>
  );
}
